/**
 * Client-side media ENCODER settings.
 *
 * These are technical knobs describing how THIS client produces files —
 * dimensions, quality, CRF, source-file memory guards, upload concurrency.
 * They are NOT business rules: slot counts, converted-size caps and the video
 * duration limit come from GET /api/medias/limits and must never be duplicated
 * here (a second copy that disagrees with the server produces 400s the admin
 * cannot interpret).
 *
 * Constants rather than env vars because this app has no runtime config
 * loader (CRA, build-time env only) and these values change rarely — a change
 * requires a rebuild either way. Values match the reference implementation's
 * production configuration (hcp-admin-frontend deploy workflow).
 */
export const MEDIA_ENCODING = {
  /** Longest edge for full-size photos (slots 1..N). */
  image_max_px: 1920,
  /** Canvas encode quality for AVIF/WebP. */
  image_quality: 0.75,
  /**
   * Longest edge for the slot-0 thumbnail. ~800px covers cards up to 400 CSS
   * pixels on a 2× display while staying ~10× lighter than a full-size photo.
   */
  thumb_max_px: 800,
  /** Video scale ceiling — never upscales a smaller source. */
  video_max_width: 1920,
  /**
   * CRF 30 is sized against the 50 MB converted cap: a 30 s clip of the
   * hardest real footage measured (~12.5 Mbps) still fits. CRF 28 would not.
   */
  video_crf: '30',
  video_preset: 'veryfast' as const,
  /** Halves the frames on 60fps phone sources; normalises VFR to CFR. */
  video_fps_cap: 30,
  /** Source-file guards protect browser/WASM memory, not server policy. */
  source_max_image_bytes: 26_214_400, // 25 MB
  source_max_video_bytes: 78_643_200, // 75 MB — covers 1080p60 for 30 s; rejects 4K
  /** Canvas-area guards — exceeding them yields a silent blank canvas on iOS. */
  max_image_pixels: 40_000_000,
  max_image_edge_px: 8192,
  /** Parallel uploads. Conversions always run one at a time. */
  upload_concurrency: 3,
};
